import React, { FC, useEffect, useState } from "react";
import { Button, Card, Modal, Nav } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useIntercom } from "react-use-intercom";
import useFlash from "go-alert/AlertMessage";
import Tag from "go-app/components/Tags/Tag";
import handleError from "go-app/services/errors";
import EmptyData from "go-core/components/EmptyData";
import { ReactComponent as LinkSVG } from "go-core/images/svg/actions/link.svg";
import AccessTokenSessionService from "go-security/services/accessTokenSessionService";
import { selectOrganization } from "go-security/services/organizations/selectors";
import AppFormatResourceStatusLabel from "../../../../../../../../components/FormatStatus";
import { ReactComponent as ChatSVG } from "../../../../../../../../images/svg/apps/chat.svg";
import { ReactComponent as HomeSVG } from "../../../../../../../../images/svg/apps/homepage.svg";
import { ReactComponent as InstalledBySVG } from "../../../../../../../../images/svg/apps/installed.svg";
import { ReactComponent as RemoveSVG } from "../../../../../../../../images/svg/apps/remove.svg";
import { ReactComponent as TagSVG } from "../../../../../../../../images/svg/apps/tag.svg";
import {
	AppStateApi,
	ApplicationApi,
	ApplicationTemplateApi,
	ApplicationUpdateApi,
} from "../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../services/Api/api";
import { AppModalViewMode, Providers } from "../../services/types";
import AppStatus from "../AppStatus";
import GastroSupplierInstallationModal from "./GastroSupplier/GastroSupplierInstallationModal";
import MultiLocationInstallModal from "./MultiLocation/MultiLocationInstallModal";
import { RenderAppIcon } from "./RenderAppIcon";

interface Props {
	onHide: () => void;
	configApp: ApplicationTemplateApi;
	installedApp?: ApplicationApi;
	viewType?: AppModalViewMode;
}

const getView = (configApp: ApplicationTemplateApi, installedApp?: ApplicationApi) => {
	if (configApp.long_description) {
		return AppModalViewMode.DESCRIPTION;
	}
	if (configApp.help_description) {
		return AppModalViewMode.START;
	}
	if (!installedApp && !configApp.long_description && !configApp.help_description) {
		return AppModalViewMode.EMPTY;
	}
	return AppModalViewMode.FORM;
};

const AppModal: FC<Props> = ({ onHide, configApp, installedApp, viewType }) => {
	const { t } = useTranslation();
	const [viewMode, setViewMode] = useState<AppModalViewMode>(
		viewType ? AppModalViewMode.FORM : getView(configApp, installedApp)
	);
	const [showModal, setShowModal] = useState(true);
	const [showLocationsModal, setShowLocationsModal] = useState(false);
	const [showGastroSupplierModal, setShowGastroSupplierModal] = useState(false);
	const [showGastroSupplierMerchantModal, setShowGastroSupplierMerchantModal] = useState(false);
	const { addFlash, addSuccessFlash } = useFlash();
	const [appState, setAppState] = useState<AppStateApi | undefined>(undefined);
	const { show } = useIntercom();
	const history = useHistory();
	const organization = useSelector(selectOrganization);

	useEffect(() => {
		fetchApplication();
	}, []);

	const fetchApplication = async () => {
		if (installedApp) {
			const params: Record<string, any> = { include: "settings" };
			const promises: [Promise<ApplicationApi>, Promise<Record<string, any>>] = [
				api.organization().getApp(installedApp.id, params),
				api.organization().getAppsConfig(Number(installedApp.id)),
			];
			try {
				const res = await Promise.all(promises);
				setAppState({
					app: res[0] as ApplicationApi,
					config: res[1],
				});
			} catch (err) {
				handleError.alert(err, addFlash);
			}
		}
	};

	const onInstall = async () => {
		const accessToken = AccessTokenSessionService.get();
		try {
			switch (configApp.provider) {
				case Providers.LOCATIONS:
					setShowModal(false);
					setShowLocationsModal(true);
					break;
				case Providers.GASTROSUPPLIER:
					setShowModal(false);
					setShowGastroSupplierModal(true);
					break;
				case Providers.DOCUMENT_TRANSFER: {
					setShowModal(false);
					const res = await api
						.organization()
						.installApps(Providers.DOCUMENT_TRANSFER, {} as ApplicationUpdateApi);
					addSuccessFlash(t("lib:common.flash.completed"));
					history.push(`/${organization.id}/apps/active/${res.id}`);
					break;
				}
				case Providers.DOCUMENT_FILE_IMPORT: {
					setShowModal(false);
					const res = await api
						.organization()
						.installApps(Providers.DOCUMENT_FILE_IMPORT, {} as ApplicationUpdateApi);
					addSuccessFlash(t("lib:common.flash.completed"));
					history.push(`/${organization.id}/apps/active/${res.id}`);
					break;
				}
				case Providers.GASTROSUPPLIER_MERCHANT:
					setShowModal(false);
					setShowGastroSupplierMerchantModal(true);
					break;
				default:
					api.organization().installApplication(configApp.provider, accessToken ? accessToken : "");
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	return (
		<>
			{showLocationsModal && <MultiLocationInstallModal onHide={onHide} />}
			{showGastroSupplierModal && (
				<GastroSupplierInstallationModal
					onHide={onHide}
					show={showGastroSupplierModal}
					provider={Providers.GASTROSUPPLIER}
				/>
			)}
			{showGastroSupplierMerchantModal && (
				<GastroSupplierInstallationModal
					onHide={onHide}
					show={showGastroSupplierMerchantModal}
					provider={Providers.GASTROSUPPLIER_MERCHANT}
				/>
			)}
			<Modal className={"app-modal"} size={"lg"} show={showModal} onHide={onHide}>
				<Modal.Header className={"modal-header"}>
					<div className={"app-icon"}>
						<RenderAppIcon app={configApp} />
					</div>
					<div className={"app-description"}>
						<div className={"app-description-header"}>
							<h5>{t(`enums.apps.providers.${configApp.provider}`)}</h5>
							<AppStatus status={appState?.app ? appState.app?.status : installedApp?.status} />
							{appState?.app && appState?.app.status !== "ENABLED" && (
								<AppFormatResourceStatusLabel
									status={appState?.app.status === "NEW" ? "INACTIVE_APP" : appState?.app.status}
								/>
							)}
							{appState?.app.link && (
								<a className={"ms-2"} href={appState?.app.link} target={"_blank"} rel="noreferrer">
									{appState?.app.name}
									<LinkSVG />
								</a>
							)}
							<RemoveSVG className={"close-icon ms-auto"} onClick={onHide} />
						</div>
						<div className={"app-description-info text-muted"}>{configApp.short_description}</div>
						<div className={"app-description-info-more"}>
							<HomeSVG style={{ marginTop: "-3px" }} /> {t("modules.app.field.created_by.title")}{" "}
							<a target={"_blank"} rel={"noreferrer"} href={configApp.owner_link}>
								{configApp.owner}
							</a>
						</div>
					</div>
				</Modal.Header>
				<Modal.Body>
					<div className={"app-modal-content"}>
						<div className={"col-md-8"}>
							<div className={"content"}>
								<div className={"content-navigation"}>
									<Nav>
										{
											<>
												{configApp.long_description && (
													<Nav.Item onClick={() => setViewMode(AppModalViewMode.DESCRIPTION)}>
														<Nav.Link
															href={"#"}
															active={viewMode === AppModalViewMode.DESCRIPTION}
														>
															{t("modules.app.field.app_description.title")}
														</Nav.Link>
													</Nav.Item>
												)}
												{configApp.help_description && (
													<Nav.Item onClick={() => setViewMode(AppModalViewMode.START)}>
														<Nav.Link
															href={"#"}
															active={viewMode === AppModalViewMode.START}
														>
															{t("modules.app.field.start.title")}
														</Nav.Link>
													</Nav.Item>
												)}
											</>
										}
									</Nav>
									<>
										{viewMode === AppModalViewMode.DESCRIPTION && (
											<Card>
												<Card.Body>
													<div
														dangerouslySetInnerHTML={{
															__html: configApp.long_description,
														}}
													/>
												</Card.Body>
											</Card>
										)}
										{viewMode === AppModalViewMode.START && (
											<Card>
												<Card.Body>
													<div
														dangerouslySetInnerHTML={{
															__html: configApp.help_description,
														}}
													/>
												</Card.Body>
											</Card>
										)}
										{viewMode === AppModalViewMode.EMPTY && (
											<EmptyData
												title={t("modules.app.field.empty.title")}
												styles={{ background: "#e6e6e6" }}
											/>
										)}
									</>
								</div>
							</div>
						</div>
						<div className={"col-md-4"}>
							<Card className={"mb-3"}>
								<Card.Body>
									{!installedApp && (
										<Button variant={"primary btn-block mb-3"} onClick={() => onInstall()}>
											{t("common.action.install", { ns: "lib" })}
										</Button>
									)}
									<div className="card-body-item">
										<InstalledBySVG />
										<div className="item-info">
											<span className="label">{t("modules.app.field.installed_by.title")}</span>
											<span>
												<div className={"action-ref"}>{configApp.owner}</div>
											</span>
										</div>
									</div>
									{configApp.tags && configApp.tags.length > 0 && (
										<div className="card-body-item">
											<TagSVG />
											<div className="item-info">
												<span className="label mb-1">
													{t("modules.app.field.categories.title")}
												</span>
												<div className={"app-tags"}>
													{configApp.tags?.map((tag, index) => (
														<Tag key={index} title={tag} />
													))}
												</div>
											</div>
										</div>
									)}
								</Card.Body>
							</Card>
							<Card className={"app-chat"}>
								<Card.Body>
									<div className="card-body-item">
										<ChatSVG />
										<div className="item-info">
											<span className="label">{t("modules.app.field.need_help.title")}</span>
										</div>
									</div>
									<Button type={"button"} variant={"add btn-block"} onClick={() => show()}>
										{t("modules.app.action.launch_chat.title")}
									</Button>
								</Card.Body>
							</Card>
							<div className={"d-flex flex-column"}>
								{configApp.rules_link && (
									<a target={"_blank"} rel={"noreferrer"} href={configApp.rules_link}>
										{t("modules.app.field.rules.title")}
									</a>
								)}
								{configApp.privacy_policy_link && (
									<a target={"_blank"} rel={"noreferrer"} href={configApp.privacy_policy_link}>
										{t("modules.app.field.privacy_policy.title")}
									</a>
								)}
							</div>
						</div>
					</div>
				</Modal.Body>
			</Modal>
		</>
	);
};
export default AppModal;
